import { useEffect, useState } from "react";

import { SignatureClient } from "@/Common/Api/Signature/SignatureClient";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PopupSection } from "@/Common/Components/Modals/PopupSection";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { usePinFrequency } from "@/Common/CustomHooks/usePinFrequency";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FileSelectionHelper } from "@/Common/Helpers/FileSelectionHelper";
import { SignatureHelper } from "@/Common/Helpers/SignatureHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { FileType } from "@/Common/Models/FileType";
import { PinFrequency } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { FeatureFlagControl } from "../FeatureFlagControl";
import { FileUploadLite } from "../FileUpload/FileUploadLite";
import { PinControl } from "./PinControl";
import { PinFrequencyControl } from "./PinFrequencyControl";
import { SignaturePinControl } from "./SignaturePinControl";
import { SignatureViewer } from "./SignatureViewer";
import { SignatureViewerThreeToOneRatio } from "./SignatureViewerThreeToOneRatio";

import styles from "./SignatureOptionPopup.module.scss";

interface ISignatureOptionPopupProps {
    employeeContactId: string;
    userId: string;
    hasPinSet: boolean;
    loginPinFrequency: LoginPinFrequencyModel;
    isObstetricalFileEnabled: boolean;
    onClose: () => void;
    onSave: (pin: string) => void;
}

function SignatureOptionPopup(props: ISignatureOptionPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const { pinFrequencyItems, pinFrequencyState } = usePinFrequency({ initialLoginPinFrequency: props.loginPinFrequency });

    const [newSignaturePath, setNewSignaturePath] = useState<string>(null);
    const [currentSignatureSource, setCurrentSignatureSource] = useState("");
    const [currentPin, setCurrentPin] = useState("");
    const [isPinValidated, setIsPinValidated] = useState(false);
    const [isSignatureResized, setIsSignatureResized] = useState(false);

    const onSave = async (): Promise<void> => {
        const pinResponse = props.hasPinSet
            ? await SignatureClient.validatePin({ PIN: currentPin, UserId: props.userId })
            : await SignatureClient.changePin({ UserId: props.userId, OldPIN: "", PIN: currentPin, IsAdminModify: false });

        if (!pinResponse) {
            displayToast(MessageToast.Error, getText("DocumentSigningPinOptions_SomethingWentWrongLabel"));
            setCurrentPin("");
            setIsPinValidated(false);
        }

        const newSignatureBase64 = newSignaturePath
            ? await SignatureClient.saveSignatureByPath(newSignaturePath)
            : currentSignatureSource; // If it is not new, then we use current one.

        const isSignatureRemovedSuccessfully = newSignaturePath || currentSignatureSource || await SignatureClient.removeSignatureImage(props.employeeContactId);

        if (newSignatureBase64 === null || !isSignatureRemovedSuccessfully) {
            displayToast(MessageToast.Error, getText("DocumentSigningPinOptions_SomethingWentWrongLabel"));
            return;
        }

        const saveLoginPinFrequencyResponse = await SignatureClient.saveLoginPinFrequency({
            UserId: props.userId,
            ConsultationRequestFrequency: pinFrequencyState.consultationRequestFrequency,
            DocumentFrequency: pinFrequencyState.documentFrequency,
            ExamRequestFrequency: pinFrequencyState.examRequestFrequency,
            FormFrequency: pinFrequencyState.formFrequency,
            PrescriptionFrequency: pinFrequencyState.prescriptionFrequency,
            ObstetricalFileFrequency: props.isObstetricalFileEnabled ? pinFrequencyState.obstetricalFileFrequency : PinFrequency.Always
        });

        if (!saveLoginPinFrequencyResponse) {
            displayToast(MessageToast.Error, getText("DocumentSigningPinOptions_SomethingWentWrongLabel"));
            return;
        }

        const saveSignatureBase64 = newSignaturePath
            ? newSignatureBase64
            : null;

        const isSignatureRemoved = !newSignaturePath && !currentSignatureSource;

        sessionStore.dispatch(
            LocalOperations.Login.UpdateSignatureByteArrayAndPinFrequency,
            {
                loginPinFrequency: saveLoginPinFrequencyResponse.LoginPinFrequency,
                signatureBase64: saveSignatureBase64,
                isSignatureRemoved
            }
        );

        sessionStore.dispatch(
            LocalOperations.Settings.UpdateSignatureAndPinFrequency,
            {
                loginPinFrequency: LoginPinFrequencyModel.fromResponseModel(saveLoginPinFrequencyResponse.LoginPinFrequency),
                signatureBase64: saveSignatureBase64,
                isSignatureRemoved
            }
        );

        props.onSave(currentPin);
        props.onClose();
    };

    const isSaveButtonDisabled = !isPinValidated || (!props.hasPinSet && !currentSignatureSource);

    useEffect(
        () => {
            const setInitialSignatureByteArray = async (): Promise<void> => {
                const base64 = await SignatureClient.loadSignatureByteArray();

                setCurrentSignatureSource(
                    SignatureHelper.getImageDataUrlFromBase64(base64) ?? ""
                );
            };

            setInitialSignatureByteArray();
        },
        []
    );

    const renderSignatureViewer = (): JSX.Element => (
        <FeatureFlagControl
            flag={cacheStore.ClinicSettings.EnableFixedSignatureRatio}
            activeFeature={(
                <SignatureViewerThreeToOneRatio
                    signatureSource={currentSignatureSource}
                    onRemoveSignature={() => {
                        setCurrentSignatureSource("");
                        setNewSignaturePath("");
                    }}
                    onImageLoaded={isImageResized => setIsSignatureResized(isImageResized)}
                />
            )}
            fallbackFeature={(
                <SignatureViewer
                    signatureSource={currentSignatureSource}
                    onRemoveSignature={() => {
                        setCurrentSignatureSource("");
                        setNewSignaturePath("");
                    }}
                />
            )}
        />
    );

    return (
        <Modal
            id="signatureOptionPopup"
            title={getText("DocumentSigningPinOptions_SignatureOptionLabel")}
            onClose={() => props.onClose()}
            footer={(
                <div className={styles.footerContainer}>
                    {(!isPinValidated && props.hasPinSet) && (
                        <div className={styles.left}>
                            <SignaturePinControl
                                onSuccessFunction={validatedPin => {
                                    setCurrentPin(validatedPin);
                                    setIsPinValidated(true);
                                }}
                                onRejectFunction={() => setIsPinValidated(false)}
                                pinWarningRenderRight
                                disableAutoFocus={!props.hasPinSet}
                            />
                        </div>
                    )}
                    <div className={styles.right}>
                        <ButtonControl
                            className="btn btn-default push-right-5"
                            onClick={() => props.onClose()}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={() => onSave()}
                            label={getText("SaveLabel")}
                            disabled={isSaveButtonDisabled}
                            dataCy="pin-frequency-popup-save-button"
                        />
                    </div>
                </div>
            )}
            width={451}
            showOverlay
            showX
            disableOverlayClose
            roundTopCorner
            draggable
            centerContent
            dataCy="pin-frequency-popup"
        >
            {!props.hasPinSet && (
                <PopupSection title={getText("DocumentSigningPinOptions_AddPinLabel")}>
                    <div className={styles.pinControlContainer}>
                        <PinControl
                            newPinText={getText("PINLabel")}
                            confirmedPinText={getText("DocumentSigningPinOptions_ConfirmPinLabel")}
                            pinSecurityText={getText("DocumentSigningPinOptions_MustContainFourDigitsLabel")}
                            pinMismatchText={getText("DocumentSigningPinOptions_PinNotMatchLabel")}
                            onPinChange={(confirmedPin, isPinValidated) => {
                                setCurrentPin(confirmedPin);
                                setIsPinValidated(isPinValidated);
                            }}
                            pinRequired={false}
                            hideAsterisk
                            hideFooter
                            isAdminReset
                        />
                    </div>
                </PopupSection>
            )}
            <PopupSection title={getText("SignatureLabel")}>
                <div className={styles.signatureContainer}>
                    <div className={styles.left}>
                        <span className="bold">{getText("PreviewLabel")}</span>
                    </div>
                    <div className={styles.right}>
                        {currentSignatureSource
                            ? renderSignatureViewer()
                            : (
                                <span className={styles.noSignatureText}>
                                    {getText("DocumentSigningPinOptions_NoSignatureLabel")}
                                </span>
                            )
                        }
                        <FileUploadLite
                            uploadButtonText={getText("UploadSignatureLabel")}
                            onCompleteCallback={path => {
                                const fileName = path.substring(path.lastIndexOf("\\"));
                                const url = apiSettings.buildAppUrl(FileSelectionHelper.getFetchUrl(fileName, FileType.TemporaryFile));
                                setCurrentSignatureSource(url);
                                setNewSignaturePath(path);
                            }}
                            fileDestinationPath={2}
                            acceptedFormat={SignatureHelper.acceptedMimeTypes}
                            className={styles.fileUploadLiteContainer}
                            isSignature
                        />
                        {cacheStore.ClinicSettings.EnableFixedSignatureRatio && isSignatureResized && (
                            <span className="red-text">
                                {getText("DocumentSigningPinOptions_SignatureWasAdjustedWarning")}
                            </span>
                        )}
                    </div>
                </div>
            </PopupSection>
            <PopupSection title={getText("DocumentSigningPinOptions_PinFrequencyLabel")}>
                <div className={styles.pinFrequencyContainer}>
                    <div className={styles.left}>
                        {pinFrequencyItems.map(p => (
                            <div key={p.title}>
                                <span>{p.title}</span>
                            </div>
                        ))}
                    </div>
                    <div className={styles.right}>
                        {pinFrequencyItems.map(p => (
                            <PinFrequencyControl
                                key={`${p.title}Control`}
                                initialPinFrequency={p.initialPinFrequency}
                                onChange={newPinFrequency => p.onChange(newPinFrequency)}
                            />
                        ))}
                    </div>
                </div>
            </PopupSection>
        </Modal>
    );
}

export { SignatureOptionPopup };
